import { email, maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Form } from 'react-final-form';
import { TextInput } from '../form/TextInput';
import { IEventNotificationService } from './event-notification-service';

export type PreaRecipientEditModalProps = {
  triggerId: number;
  actionId: number;
  initialName: string;
  initialEmail: string;
  eventNotificationService: IEventNotificationService;
  onClose(success?: boolean): void;
};

type FormValues = {
  name: string;
  email: string;
};

const save = async (
  data: FormValues,
  props: PreaRecipientEditModalProps
): Promise<void | { [FORM_ERROR]: string }> => {
  try {
    const response = await props.eventNotificationService.updatePREAAction({
      triggerId: props.triggerId,
      emailAction: {
        actionId: props.actionId,
        name: data.name,
        address: data.email,
      },
    });
    if (!response.data || !response.data.success) {
      return { [FORM_ERROR]: 'An unexpected error occurred' };
    }
  } catch (err) {
    const ax = err as AxiosError;
    const status = ax.response?.status ?? (err as { status?: number }).status;
    if (status === 400) {
      return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
    }
    return { [FORM_ERROR]: 'An unexpected error occurred' };
  }
};

const PreaRecipientEditModal: React.FC<PreaRecipientEditModalProps> = (props) => {
  const initialValues: FormValues = {
    name: props.initialName,
    email: props.initialEmail,
  };

  return (
    <Modal show onHide={() => props.onClose(false)}>
      <Form
        initialValues={initialValues}
        onSubmit={async (data: FormValues) => {
          const result = await save(data, props);
          if (result != null && typeof result === 'object' && FORM_ERROR in result) {
            return result;
          }
          props.onClose(true);
          return undefined;
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                Edit PREA recipient
                {submitting && (
                  <>
                    {' '}
                    <i className="fas fa-spin fa-spinner" />
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError != null && <Alert variant="danger">{String(submitError)}</Alert>}
              <Row>
                <Col md={6}>
                  <TextInput
                    label="Name"
                    name="name"
                    validationRules={[required, minLength(1), maxLength(200)]}
                  />
                </Col>
                <Col md={6}>
                  <TextInput
                    label="Email"
                    name="email"
                    validationRules={[required, minLength(1), maxLength(320), email]}
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="default" type="button" disabled={submitting} onClick={() => props.onClose(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting || hasValidationErrors}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

export default PreaRecipientEditModal;
